// src/components/Sidebar.jsx
import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  FileText,
  History,
  Package,
  PackagePlus,
  Building2,
  UserPlus,
  Users,
  CircleUserRound,
  LogOut,
  Menu,
  X,
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
} from "lucide-react";
import LogoMark from "../component/ui/LogoMark";

// Nav is grouped into ERP-style sections (Sales / Catalog / Customers /
// Partners / Account) rather than one flat list, and every item gets its
// own icon (previously every single item reused the same icon).
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, roles: ["user", "admin", "partner"] },
    ],
  },
  {
    label: "Sales",
    items: [
      { name: "Create PI", path: "/quotation", icon: FileText, roles: ["user", "admin", "partner"] },
      { name: "PI History", path: "/history", icon: History, roles: ["admin", "partner"] },
    ],
  },
  {
    label: "Catalog",
    items: [
      { name: "All Products", path: "/all-products", icon: Package, roles: ["user", "partner"] },
      { name: "Add Product", path: "/add-product", icon: PackagePlus, roles: ["admin"] },
    ],
  },
  {
    label: "Customers",
    items: [
      { name: "Business Directory", path: "/all-user", icon: Building2, roles: ["admin", "partner"] },
      { name: "Add Customer", path: "/new-user", icon: UserPlus, roles: ["admin", "partner"] },
    ],
  },
  {
    label: "Partners",
    items: [{ name: "All Partners", path: "/all-partner", icon: Users, roles: ["admin"] }],
  },
  {
    label: "Account",
    items: [{ name: "Profile", path: "/profile", icon: CircleUserRound, roles: ["user", "partner"] }],
  },
];

const ROLE_LABEL = { admin: "Administrator", partner: "Partner", user: "Customer" };

const Sidebar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path) => location.pathname === path;

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.roles.includes(user?.role)),
  })).filter((section) => section.items.length > 0);

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between bg-ink-950 text-white px-4 py-3">
        <button onClick={() => setIsOpen(!isOpen)} aria-label="Toggle navigation" className="p-1">
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2">
          <LogoMark size={26} />
          <span className="text-sm font-semibold text-white tracking-tight">Landschaft</span>
        </div>
        <span className="text-xs text-ink-300 truncate max-w-[90px]">{user?.email}</span>
      </div>

      {/* Overlay (mobile) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-10 md:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full ${collapsed ? "w-[76px]" : "w-64"} bg-ink-950 text-ink-100 flex flex-col shadow-xl transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } transition-all duration-200 ease-in-out z-20 md:translate-x-0 md:static md:shadow-none shrink-0`}
      >
        {/* Brand - LogoMark crops the source image to just the teal icon
            mark, which has enough contrast to sit directly on this dark
            sidebar; "Landschaft" is set as real text rather than baked
            into the (illegible-here) wordmark part of the source image. */}
        <div className={`flex items-center border-b border-white/10 h-16 shrink-0 gap-2.5 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <LogoMark size={collapsed ? 30 : 34} />
            {!collapsed && (
              <span className="text-base font-semibold text-white tracking-tight truncate">Landschaft</span>
            )}
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3 space-y-5">
          {visibleSections.map((section) => (
            <div key={section.label}>
              {!collapsed && (
                <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
                  {section.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        title={collapsed ? item.name : undefined}
                        className={`group flex items-center gap-3 rounded-md text-sm font-medium transition-colors relative ${
                          collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
                        } ${
                          active
                            ? "bg-white/10 text-white"
                            : "text-ink-300 hover:bg-white/5 hover:text-white"
                        }`}
                        aria-current={active ? "page" : undefined}
                      >
                        {active && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-blue-500" />
                        )}
                        <item.icon size={17} className={active ? "text-blue-400" : "text-ink-400 group-hover:text-ink-200"} />
                        {!collapsed && <span className="truncate">{item.name}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="hidden md:flex items-center justify-center gap-2 mx-3 mb-2 py-2 rounded-md text-ink-400 hover:bg-white/5 hover:text-white text-xs transition-colors"
        >
          {collapsed ? <ChevronsRight size={15} /> : <><ChevronsLeft size={15} /> Collapse</>}
        </button>

        {/* User + logout */}
        <div className={`border-t border-white/10 p-3 ${collapsed ? "flex justify-center" : ""}`}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
              <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold text-white shrink-0">
                {(user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.email}</p>
                <p className="text-[10px] text-ink-400">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            title="Logout"
            className={`w-full flex items-center gap-3 rounded-md text-sm font-medium text-ink-300 hover:bg-white/5 hover:text-white transition-colors ${
              collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <LogOut size={17} />
            {!collapsed && "Logout"}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
